import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNotification } from '../context/NotificationContext';
import { normalizeMobileNumber, convertPersianToEnglish, formatMobileForDisplay } from '../utils/mobileUtils';
import { apiFetch } from '../utils/api';
import { userFacingApiError } from '../utils/userMessages';
import './Login.css';

function Login() {
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [canResend, setCanResend] = useState(false);
  const { login } = useAuth();
  const { isDarkMode } = useTheme();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();
    const normalizedMobile = normalizeMobileNumber(mobile);
    if (!normalizedMobile) {
      showNotification('لطفاً شماره موبایل معتبر وارد کنید', 'error');
      return;
    }
    setLoading(true);
    try {
      const response = await apiFetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: normalizedMobile })
      });
      const data = await response.json();
      if (response.ok) {
        setStep(2);
        setTimeLeft(120);
        setCanResend(false);
        showNotification('کد تایید ارسال شد', 'success');
      } else {
        showNotification(userFacingApiError(data, 'خطا در ارسال کد'), 'error');
      }
    } catch (error) {
      showNotification('خطا در ارسال کد', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step === 2 && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResendCode = async (e) => {
    if (e) e.preventDefault();
    setCanResend(false);
    setTimeLeft(120);
    setCode('');
    setLoading(true);
    try {
      const normalizedMobile = normalizeMobileNumber(mobile);
      if (!normalizedMobile) {
        showNotification('لطفاً شماره موبایل معتبر وارد کنید', 'error');
        return;
      }
      const response = await apiFetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: normalizedMobile })
      });
      const data = await response.json();
      if (response.ok) {
        setTimeLeft(120);
        setCanResend(false);
        showNotification('کد تایید مجدداً ارسال شد', 'success');
      } else {
        showNotification(userFacingApiError(data, 'خطا در ارسال کد'), 'error');
      }
    } catch (error) {
      showNotification('خطا در ارسال کد', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const normalizedCode = convertPersianToEnglish(code);
    if (!normalizedCode || normalizedCode.length !== 4) {
      showNotification('لطفاً کد 4 رقمی را وارد کنید', 'error');
      return;
    }
    const normalizedMobile = normalizeMobileNumber(mobile);
    if (!normalizedMobile) {
      showNotification('شماره موبایل نامعتبر است', 'error');
      return;
    }
    setLoading(true);
    try {
      const response = await apiFetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: normalizedMobile, code: normalizedCode })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        login(data.user);
        setTimeLeft(0);
        setCanResend(false);
        showNotification('ورود با موفقیت انجام شد', 'success');
        navigate('/dashboard');
      } else {
        showNotification(userFacingApiError(data, 'کد تایید نامعتبر یا منقضی شده است'), 'error');
      }
    } catch (error) {
      showNotification('خطا در تایید کد', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`login-container ${isDarkMode ? 'dark' : ''}`}>
      <div className="login-box">
        <h1>ورود / ثبت نام</h1>
        {step === 1 && (
          <form onSubmit={handleSendCode}>
            <div className="form-group">
              <label>شماره موبایل</label>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                value={mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9۰-۹]/g, '');
                  setMobile(value);
                }}
                placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                maxLength="11"
                required
              />
            </div>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'در حال ارسال...' : 'ارسال کد تایید'}
            </button>
          </form>
        )}
        {step === 2 && (
          <form onSubmit={handleVerifyCode}>
            <div className="form-group">
              <label>کد تایید ارسال شده</label>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9۰-۹]/g, '');
                  setCode(value);
                }}
                placeholder="۱۲۳۴"
                maxLength="4"
                required
              />
              <p className="help-text">کد به شماره {formatMobileForDisplay(mobile) || mobile} ارسال شد</p>
              {timeLeft > 0 && <p className="timer-text">زمان باقی‌مانده: {formatTime(timeLeft)}</p>}
              {canResend && <p className="expired-text">کد تایید منقضی شده است</p>}
            </div>
            <button type="submit" className="btn-primary" disabled={loading || canResend}>
              {loading ? 'در حال تایید...' : 'تایید کد'}
            </button>
            {canResend ? (
              <button type="button" className="btn-primary" onClick={handleResendCode} disabled={loading}>
                {loading ? 'در حال ارسال...' : 'ارسال مجدد کد'}
              </button>
            ) : (
              <button type="button" className="btn-secondary" onClick={() => { setStep(1); setTimeLeft(120); setCanResend(false); setCode(''); }}>
                تغییر شماره موبایل
              </button>
            )}
          </form>
        )}
        <button className="back-link" onClick={() => navigate(-1)}>→ بازگشت</button>
      </div>
    </div>
  );
}

export default Login;
